import { Button } from "@/components/ui/Button";
import Icon from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
import { isLocal } from "@/lib/utils/env";
import { ErrorBoundaryFallbackProps } from "./types";

const ErrorBoundaryFallback = ({ error, resetErrorBoundary, className }: ErrorBoundaryFallbackProps) => {
  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className={cn("flex min-h-screen items-center justify-center p-4", className)}>
      <main
        className="flex w-full max-w-2xl flex-col items-center space-y-6 rounded-2xl border border-white/10 bg-card-bg p-4 text-center lg:p-8"
        role="main"
        aria-labelledby="error-heading"
        aria-describedby="error-description"
      >
        <Icon name="exclamation-mark-outline" className="h-16 w-16 text-white" aria-hidden="true" />

        <div className="space-y-2">
          <h1 id="error-heading" className="font-['Inter'] text-2xl font-semibold text-white">
            Oops! Something went wrong
          </h1>
          <p id="error-description" className="font-['Inter'] text-base text-light-gray">
            We encountered an unexpected error. Don't worry, this has been logged and our team will look into it.
          </p>
        </div>

        {isLocal && error && (
          <section className="w-full space-y-2" aria-labelledby="error-details-heading">
            <details className="w-full">
              <summary
                id="error-details-heading"
                className="cursor-pointer text-sm text-light-gray hover:text-white"
                aria-expanded="false"
              >
                View error details
              </summary>
              <div className="mt-2 rounded-lg bg-[#0A0B0D] p-3 text-left" role="region" aria-label="Error details">
                <p className="break-words text-xs text-error-light">
                  <strong>Error:</strong> {error.message}
                </p>
                {error.stack && (
                  <pre
                    className="mt-2 overflow-auto whitespace-pre-wrap text-xs text-light-gray"
                    aria-label="Error stack trace"
                  >
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>
          </section>
        )}

        <nav className="flex w-full flex-col space-y-3" aria-label="Error page actions">
          <Button onClick={handleReload} className="w-full" aria-describedby="try-again-description">
            Try Again
          </Button>
          <span id="try-again-description" className="sr-only">
            Refresh the page to attempt loading again
          </span>

          <Button
            onClick={() => (window.location.href = "/")}
            variant="outline"
            color="secondary"
            className="w-full"
            aria-describedby="dashboard-description"
          >
            Go to Dashboard
          </Button>
          <span id="dashboard-description" className="sr-only">
            Navigate back to the main dashboard page
          </span>
        </nav>
      </main>
    </div>
  );
};

export default ErrorBoundaryFallback;
